import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Calendar, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';

const caseStudies = {
  'ithi-vscode': {
    title: 'Ithi AI Code Translator',
    subtitle: 'AI-powered VS Code extension for multilingual code comments',
    year: '2024',
    category: 'AI Tools',
    tags: ['TypeScript', 'React', 'AI', 'VS Code API', 'AST'],
    overview: 'Developed an AI-powered extension for Microsoft VS Code Marketplace that translates code comments across 160+ languages while preserving code identifiers and syntax with custom AST-aware masking.',
    challenge: [
      'Code comments needed to be translated without breaking syntax or modifying identifiers',
      'Supporting 160+ languages while maintaining code integrity',
      'Ensuring security with Content Security Policy in VS Code webview',
      'Creating a seamless developer experience within the IDE'
    ],
    solution: [
      'Implemented custom AST-aware masking to identify and protect code identifiers during translation',
      'Built two-pass restoration system to accurately reconstruct translated comments',
      'Secured the extension with nonce-based CSP and typed bridge for webview communication',
      'Created React-based webview UI for intuitive translation controls',
      'Integrated with AI translation APIs for high-quality multilingual support'
    ],
    results: [
      'Successfully published on VS Code Marketplace',
      'Supports 160+ languages with syntax preservation',
      'Enables seamless collaboration across global development teams',
      'Zero code-breaking incidents in production use',
      'Positive user feedback on developer experience'
    ],
    technicalHighlights: [
      'Custom Abstract Syntax Tree (AST) parsing for identifier detection',
      'Nonce-based Content Security Policy implementation',
      'TypeScript for type-safe VS Code extension development',
      'React webview integration with typed messaging bridge',
      'Two-pass translation and restoration algorithm'
    ]
  },
  'zodia': {
    title: 'Zodia',
    subtitle: 'Full-stack app with AI-powered retrieval-augmented responses',
    year: '2024',
    category: 'Full-Stack',
    tags: ['React', 'TypeScript', 'Node.js', 'MongoDB', 'Pinecone', 'AI'],
    overview: 'Built a full-stack application featuring retrieval-augmented AI responses, custom UX design, and cross-platform media players for web and mobile.',
    challenge: [
      'Delivering accurate, context-aware AI responses using retrieval-augmented generation',
      'Creating consistent experience across web and mobile platforms',
      'Designing intuitive UX for complex AI interactions',
      'Managing vector embeddings and semantic search at scale',
      'Ensuring fast response times for real-time user interactions'
    ],
    solution: [
      'Implemented retrieval-augmented generation (RAG) using Pinecone vector database',
      'Built React frontend with TypeScript for type-safe component architecture',
      'Developed Node.js/Express backend with MongoDB for data persistence',
      'Created custom media players optimized for web and mobile experiences',
      'Designed clean, intuitive UX focusing on conversational AI patterns',
      'Optimized vector search queries for sub-second response times'
    ],
    results: [
      'Achieved accurate, contextually relevant AI responses using RAG architecture',
      'Delivered seamless cross-platform experience (web + mobile)',
      'User engagement increased through intuitive UX design',
      'Sub-second query response times with optimized vector search',
      'Scalable architecture supporting growing content library'
    ],
    technicalHighlights: [
      'Retrieval-Augmented Generation (RAG) architecture',
      'Pinecone vector database for semantic search',
      'React + TypeScript frontend with reusable components',
      'Node.js/Express REST API',
      'MongoDB for flexible document storage',
      'Custom media player implementations'
    ]
  }
};

const CaseStudy = () => {
  const { id } = useParams();
  const study = caseStudies[id];

  if (!study) {
    return (
      <main id="main-content" className="pt-24 sm:pt-32 pb-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl font-bold mb-4">Case Study Not Found</h1>
          <Button asChild>
            <Link to="/projects">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Projects
            </Link>
          </Button>
        </div>
      </main>
    );
  }

  return (
    <main id="main-content" className="pt-24 sm:pt-32 pb-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <div className="max-w-4xl mx-auto mb-8">
          <Button asChild variant="ghost" size="sm">
            <Link to="/projects">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Projects
            </Link>
          </Button>
        </div>

        {/* Header */}
        <section className="max-w-4xl mx-auto mb-12">
          <div className="space-y-6">
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">{study.category}</Badge>
              <Badge variant="outline" className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {study.year}
              </Badge>
            </div>
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-3">
                {study.title}
              </h1>
              <p className="text-xl text-muted-foreground">{study.subtitle}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {study.tags.map((tag, idx) => (
                <Badge key={idx} variant="outline" className="flex items-center gap-1">
                  <Tag className="h-3 w-3" />
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        </section>

        {/* Overview */}
        <section className="max-w-4xl mx-auto mb-12">
          <Card>
            <CardContent className="p-6 sm:p-8">
              <h2 className="text-2xl font-bold mb-4">Overview</h2>
              <p className="text-muted-foreground leading-relaxed">{study.overview}</p>
            </CardContent>
          </Card>
        </section>

        {/* Challenge */}
        <section className="max-w-4xl mx-auto mb-12">
          <h2 className="text-2xl font-bold mb-6">The Challenge</h2>
          <Card>
            <CardContent className="p-6 sm:p-8">
              <ul className="space-y-3">
                {study.challenge.map((item, idx) => (
                  <li key={idx} className="flex gap-3 text-muted-foreground">
                    <span className="text-primary font-bold mt-1">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </section>

        {/* Solution */}
        <section className="max-w-4xl mx-auto mb-12">
          <h2 className="text-2xl font-bold mb-6">The Solution</h2>
          <Card>
            <CardContent className="p-6 sm:p-8">
              <ul className="space-y-3">
                {study.solution.map((item, idx) => (
                  <li key={idx} className="flex gap-3 text-muted-foreground">
                    <span className="text-primary font-bold mt-1">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </section>

        {/* Results */}
        <section className="max-w-4xl mx-auto mb-12">
          <h2 className="text-2xl font-bold mb-6">Results & Impact</h2>
          <Card className="border-2 border-primary/20">
            <CardContent className="p-6 sm:p-8">
              <ul className="space-y-3">
                {study.results.map((item, idx) => (
                  <li key={idx} className="flex gap-3 text-muted-foreground">
                    <span className="text-primary font-bold mt-1">✓</span>
                    <span className="font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </section>

        {/* Technical Highlights */}
        <section className="max-w-4xl mx-auto mb-12">
          <h2 className="text-2xl font-bold mb-6">Technical Highlights</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {study.technicalHighlights.map((highlight, idx) => (
              <Card key={idx}>
                <CardContent className="p-4">
                  <p className="text-sm font-medium">{highlight}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <Separator className="max-w-4xl mx-auto mb-12" />

        {/* CTA */}
        <section className="max-w-4xl mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4">Interested in similar solutions?</h3>
          <p className="text-muted-foreground mb-6">
            Let's discuss how I can help with your next project.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg">
              <Link to="/contact">
                Get in Touch
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/projects">
                View More Projects
              </Link>
            </Button>
          </div>
        </section>
      </div>
    </main>
  );
};

export default CaseStudy;
